import { ChevronDownIcon, InfoIcon } from 'lucide-react';

import { CarrierVerificationInfo } from 'types/CarrierVerification';
import { Maybe } from 'types/UtilityTypes';

import { Button } from '../Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { Flex } from '../layout';
import { Typography } from '../typography';

interface CarrierVerificationMultipleCarriersSelectorProps {
  allCarriers: CarrierVerificationInfo[];
  totalCarriersFound?: number;
  selectedCarrierName: Maybe<string>;
  onCarrierSelect: (index: number) => void;
}

export default function CarrierVerificationMultipleCarriersSelector({
  allCarriers,
  totalCarriersFound,
  selectedCarrierName,
  onCarrierSelect,
}: CarrierVerificationMultipleCarriersSelectorProps) {
  return (
    <div className='w-full mb-2'>
      <Flex
        align='center'
        gap='sm'
        className='rounded-lg border border-info-200 bg-info-50 p-2 mb-2'
      >
        <InfoIcon className='h-4 w-4 text-info-600 shrink-0' />
        <Typography variant='body-sm' className='text-info-600'>
          Multiple carriers found ({totalCarriersFound || allCarriers.length}{' '}
          total)
        </Typography>
      </Flex>
      <div className='w-full'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              className='w-full justify-between'
              buttonNamePosthog={null}
            >
              <Typography variant='body-sm' className='truncate'>
                {selectedCarrierName || 'Select a carrier'}
              </Typography>
              <ChevronDownIcon className='h-4 w-4 shrink-0' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='w-full max-w-md'>
            {allCarriers.map((carrierInfo, index) => (
              <DropdownMenuItem
                key={index}
                onClick={() => onCarrierSelect(index)}
                className='cursor-pointer'
              >
                <Flex direction='col' align='start' gap='xs' className='w-full'>
                  <Typography variant='body-sm' className='truncate w-full'>
                    {carrierInfo.name}
                  </Typography>
                  {carrierInfo.dba && (
                    <Typography
                      variant='body-xs'
                      textColor='muted'
                      className='truncate w-full'
                    >
                      DBA: {carrierInfo.dba}
                    </Typography>
                  )}
                  <Flex direction='row' gap='sm' className='mt-1'>
                    <Typography variant='body-xs' textColor='muted'>
                      DOT: {carrierInfo.dot_number}
                    </Typography>
                    {carrierInfo.docket_number && (
                      <Typography variant='body-xs' textColor='muted'>
                        MC: {carrierInfo.docket_number}
                      </Typography>
                    )}
                  </Flex>
                </Flex>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
